import { useCallback, useSyncExternalStore } from 'react';

/**
 * Subscribes to a CSS media query and re-renders only when it flips.
 *
 * Backed by `useSyncExternalStore`, so the first render already reads the real
 * match instead of defaulting to `false` — layout branches (mobile card list vs
 * desktop table) paint correctly without a flash.
 *
 * @param query - a CSS media query string, e.g. `(max-width: 767px)`
 * @returns whether the document currently matches the query
 */
export const useMediaQuery = (query: string): boolean => {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const mediaQueryList = window.matchMedia(query);
      mediaQueryList.addEventListener('change', onStoreChange);
      return () => mediaQueryList.removeEventListener('change', onStoreChange);
    },
    [query],
  );

  const getSnapshot = useCallback(
    () => window.matchMedia(query).matches,
    [query],
  );

  return useSyncExternalStore(subscribe, getSnapshot, () => false);
};
